import { Heading, LuIcon, Separator } from "@overmap-ai/blocks"
import { OrganizationAccessLevel, type ProcedureTypeAttachment, type Stored } from "@overmap-ai/common"
import { selectAttachmentsOfProcedureType, selectProcedureTypeById } from "@overmap-ai/core"
import { getRouteApi } from "@tanstack/react-router"
import { memo, useCallback } from "react"

import { AttachmentList } from "../../components/AttachmentList/AttachmentList"
import { AttachmentButton } from "../../components/Buttons/AttachmentButton"
import { ViewContainer } from "../../components/Containers/ViewContainer"
import { OrganizationAccessRequired } from "../../components/OrganizationAccessRequired/AccessRequired"
import { useConfirmDeleteDialog } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import { useAppSelector, useShallowEqaulSelector } from "../../store"

const routeApi = getRouteApi(
	"/_loggedIn/$organizationId/_map/_layout/settings/procedure-types/$procedureTypeId/attachments",
)

export const ProcedureTypeSettingsAttachmentsView = memo(() => {
	const { procedureTypeId } = routeApi.useParams()

	const procedureType = useAppSelector(selectProcedureTypeById(procedureTypeId))

	if (!procedureType) {
		throw new Error(`expected procedure type to exist, got ${procedureType}.`)
	}

	const attachments = useShallowEqaulSelector(selectAttachmentsOfProcedureType(procedureType.uuid))
	const openConfirmDeleteDialog = useConfirmDeleteDialog()

	const handleAttachFiles = useCallback(
		(files: File[]) => {
			return sdk.procedureTypeAttachments.bulkAdd(
				files.map((file) => ({
					procedure_type: procedureType.uuid,
					file,
				})),
			)
		},
		[procedureType.uuid],
	)

	const handleDeleteAttachment = useCallback(
		(fileAttachment: Stored<ProcedureTypeAttachment>) => {
			openConfirmDeleteDialog({
				title: "Delete attachment",
				description: "The attachment will be deleted immediately. This action cannot be undone.",
				onAction: () => sdk.procedureTypeAttachments.delete(fileAttachment.uuid),
			})
		},
		[openConfirmDeleteDialog],
	)

	return (
		<ViewContainer maxWidth="4xl">
			<section className="flex size-full flex-col gap-4">
				<div className="flex items-center justify-between gap-2">
					<Heading size="xl">Attachments</Heading>

					<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.ADMIN} type="hide">
						<AttachmentButton size="sm" variant="surface" onAttachFiles={handleAttachFiles}>
							<LuIcon icon={modelIconMapping.add} />
							Add
						</AttachmentButton>
					</OrganizationAccessRequired>
				</div>

				<Separator size="full" />

				<AttachmentList
					attachments={attachments}
					onDelete={handleDeleteAttachment}
					service={sdk.procedureTypeAttachments}
				/>
			</section>
		</ViewContainer>
	)
})
ProcedureTypeSettingsAttachmentsView.displayName = "ProcedureTypeSettingsAttachmentsView"
